/* eslint-disable default-param-last */
import { Vector3 } from 'three';
import { ITexture } from '../../@types/ContextProviderRelated';
import { IManufacturer } from '../../@types/FilesAPIModels';
import { DEFAULT_EMPTY_TEXTURE } from '../../utils/constants';
import { serializeTexture } from '../../utils/export/serializeTexture';
import Entity, { IEntity } from '../abstract/Entity';
import { IAsset } from './AssetModel';
import AttributeModel from './AttributeModel';
import { IObject, ObjectModel } from './ObjectModel';

export interface IProduct extends IEntity {
  asset: IAsset | undefined;
  id: string;
  name: string;
  svgPath: string;
  dimensions: { length: number; height?: number; depth: number };
  position: Vector3;
  rotation: number;
  fillColor?: string;
  strokeColor?: string;
  strokeWidth: number;
  texture?: ITexture | null;
  objects?: Array<IObject>;
  description?: string;
  manufacturer?: string | IManufacturer;
  country?: string; // Added
}

class ProductModel extends Entity implements IProduct {
  asset: IAsset | undefined;
  id: string;
  name: string;
  svgPath: string;
  dimensions: { length: number; height?: number; depth: number };
  position: Vector3;
  rotation: number;
  fillColor?: string;
  strokeColor?: string;
  strokeWidth: number;
  texture?: ITexture | null;
  objects?: Array<IObject>;
  description?: string;
  manufacturer?: string | IManufacturer;
  country?: string;

  constructor(
    id: string,
    name: string,
    svgPath: string,
    length: number,
    height: number,
    depth: number,
    position: Vector3 = new Vector3(0, 0, 0.1),
    rotation: number = 0,
    fillColor?: string,
    strokeColor?: string,
    strokeWidth: number = 1.5,
    texture?: ITexture | null,
    attributes: AttributeModel[] = [],
    objects: ObjectModel[] = [],
    description?: string,
    manufacturer?: string | IManufacturer,
    uuid: string | undefined = undefined,
    country?: string,
  ) {
    super(attributes);
    if (uuid) {
      this.uuid = uuid;
    }
    this.id = id;
    this.name = name;
    this.svgPath = svgPath;
    this.dimensions = { length, height, depth };
    this.position = position;
    this.rotation = rotation;
    this.fillColor = fillColor;
    this.strokeColor = strokeColor;
    this.strokeWidth = strokeWidth;
    this.texture = texture ?? DEFAULT_EMPTY_TEXTURE;
    this.objects = objects;
    this.description = description;
    this.manufacturer = manufacturer;
    this.country = country;
  }

  getAsset(): IAsset | undefined {
    return this.asset;
  }
  setAsset(value: IAsset | undefined): void {
    this.asset = value;
  }
  hasAsset(): boolean {
    return this.asset !== undefined;
  }

  getId(): string {
    return this.id;
  }
  setId(value: string): void {
    this.id = value;
  }

  getName(): string {
    return this.name;
  }
  setName(value: string): void {
    this.name = value;
  }

  getSvgPath(): string {
    return this.svgPath;
  }
  setSvgPath(value: string): void {
    this.svgPath = value;
  }

  getDimensions(): { length: number; height?: number; depth: number } {
    return this.dimensions;
  }
  setDimensions(value: { length: number; height?: number; depth: number }): void {
    this.dimensions = value;
  }

  getPosition(): Vector3 {
    return this.position;
  }
  setPosition(value: Vector3): void {
    this.position = value;
  }

  getRotation(): number {
    return this.rotation;
  }
  setRotation(value: number): void {
    this.rotation = value;
  }

  getFillColor(): string | undefined {
    return this.fillColor;
  }
  setFillColor(value: string | undefined): void {
    this.fillColor = value;
  }

  getStrokeColor(): string | undefined {
    return this.strokeColor;
  }
  setStrokeColor(value: string | undefined): void {
    this.strokeColor = value;
  }

  getStrokeWidth(): number {
    return this.strokeWidth;
  }
  setStrokeWidth(value: number): void {
    this.strokeWidth = value;
  }

  getTexture(): ITexture | null | undefined {
    return this.texture;
  }
  setTexture(value: ITexture | undefined): void {
    this.texture = value;
  }

  getObjects(): Array<IObject> | undefined {
    return this.objects;
  }
  setObjects(objects: Array<ObjectModel> | undefined) {
    this.objects = objects;
  }

  getDescription(): string | undefined {
    return this.description;
  }
  setDescription(description: string | undefined) {
    this.description = description;
  }

  getManufacturer(): string | IManufacturer | undefined {
    return this.manufacturer;
  }
  setManufacturer(manufacturer: string | IManufacturer | undefined) {
    this.manufacturer = manufacturer;
  }

  getCountry(): string | undefined {
    return this.country;
  }
  setCountry(value: string | undefined) {
    this.country = value;
  }

  updatePosition(newPosition: Vector3) {
    this.position = newPosition;
  }
  updateRotation(newRotation: number) {
    this.rotation = newRotation;
  }

  /** Serialize the product data to JSON */
  toJSON() {
    return {
      ...super.toJSON(),
      id: this.id,
      name: this.name,
      dimensions: this.dimensions,
      position: {
        x: this.position.x,
        y: this.position.y,
        z: this.position.z,
      },
      rotation: this.rotation,
      fillColor: this.fillColor,
      strokeColor: this.strokeColor,
      strokeWidth: this.strokeWidth,
      texture: serializeTexture(this.texture),
      objects: this.objects?.map((o) => (typeof o.toJSON === 'function' ? o.toJSON() : o)),
      description: this.description,
      manufacturer: this.manufacturer,
      country: this.country,
    };
  }
}

export default ProductModel;
